/**
 * Checks if a key represents a latitude field
 */
export function isLatitudeKey(key: string): boolean {
  return key.toLowerCase().includes('latitude') && !key.toLowerCase().includes('longitude')
}

/**
 * Checks if a key represents a longitude field
 */
export function isLongitudeKey(key: string): boolean {
  return key.toLowerCase().includes('longitude')
}

/**
 * Gets the paired latitude/longitude key
 * e.g., "homeBaseLatitude" -> "homeBaseLongitude"
 *       "latitude" -> "longitude"
 */
export function getPairedGeoKey(key: string): string | null {
  if (isLatitudeKey(key)) {
    return key.replace(/latitude/gi, 'longitude')
  }
  if (isLongitudeKey(key)) {
    return key.replace(/longitude/gi, 'latitude')
  }
  return null
}

/**
 * Formats latitude and longitude values as "Lat, Long"
 */
export function formatGeoLocation(lat: any, lng: any): string {
  if ((lat === null || lat === undefined || lat === '' || lat === 'null') &&
      (lng === null || lng === undefined || lng === '' || lng === 'null')) {
    return '-'
  }
  
  const latValue = lat !== null && lat !== undefined && lat !== '' && lat !== 'null' 
    ? Number(lat).toFixed(6) 
    : null
  const lngValue = lng !== null && lng !== undefined && lng !== '' && lng !== 'null'
    ? Number(lng).toFixed(6)
    : null
    
  if (latValue && lngValue) {
    return `${latValue}, ${lngValue}`
  }
  if (latValue) {
    return `${latValue}, -`
  }
  if (lngValue) {
    return `-, ${lngValue}`
  }
  return '-'
}

/**
 * Formats internal attribute names to user-friendly display names
 * Examples:
 * - businessName -> "Business Name"
 * - plateNo -> "Plate Number"
 * - lengthM -> "Length (Meters)"
 * - createdAt -> "Created At"
 * - latitude/longitude -> "Geo Location"
 */
export function formatAttributeName(key: string): string {
  // Special mappings for common abbreviations and terms
  const specialMappings: Record<string, string> = {
    // Account fields
    businessName: 'Business Name',
    businessAddress: 'Business Address',
    contactEmail: 'Contact Email',
    contactPhone: 'Contact Phone',
    vatNum: 'VAT Number',
    billingAddress: 'Billing Address',
    businessLegalId: 'Business Legal ID',
    paymentMethod: 'Payment Method',
    defaultPaymentTerms: 'Default Payment Terms',
    defaultCurrency: 'Default Currency',
    subscriptionStartDate: 'Subscription Start Date',
    subscriptionEndDate: 'Subscription End Date',
    organizationCode: 'Organization Code',
    isActive: 'Status',
    accountId: 'Account ID',
    
    // Vehicle fields
    plateNo: 'Plate Number',
    plateNumber: 'Plate Number',
    vehicleType: 'Vehicle Type',
    make: 'Make',
    model: 'Model',
    year: 'Year',
    color: 'Color',
    lengthM: 'Length (Meters)',
    widthM: 'Width (Meters)',
    heightM: 'Height (Meters)',
    weightKg: 'Weight (Kilograms)',
    capacityKg: 'Capacity (Kilograms)',
    capacityM3: 'Capacity (Cubic Meters)',
    fuelType: 'Fuel Type',
    licenseNumber: 'License Number',
    insuranceNumber: 'Insurance Number',
    registrationDate: 'Registration Date',
    expiryDate: 'Expiry Date',
    
    // Driver fields
    driverLicense: 'Driver License',
    licenseExpiry: 'License Expiry',
    dateOfBirth: 'Date of Birth',
    nationalId: 'National ID',
    
    // User fields
    profileImageKey: 'Profile Image',
    phoneNumber: 'Phone Number',
    
    // Common fields
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    createdBy: 'Created By',
    updatedBy: 'Updated By',
    id: 'ID',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    status: 'Status',
    type: 'Type',
    rating: 'Rating',
    score: 'Score',
    kycStatus: 'KYC Status',
    
    // User nested fields (for driver.user.*)
    'user.name': 'User Name',
    'user.email': 'User Email',
    'user.phone': 'User Phone',
    'user.phoneNumber': 'User Phone Number',
    'user.status': 'User Status',
    'user.roles': 'User Roles',
    'user.profileImageKey': 'User Profile Image',
    'user.createdAt': 'User Created At',
    'user.updatedAt': 'User Updated At',
  }
  
  // Handle latitude/longitude - combine into "Geo Location"
  if (isLatitudeKey(key) || isLongitudeKey(key)) {
    // Extract the prefix (e.g., "homeBase" from "homeBaseLatitude")
    const prefix = key.replace(/latitude|longitude/gi, '').trim()
    if (prefix) {
      return `${formatAttributeName(prefix)} Geo Location`
    }
    return 'Geo Location'
  }
  
  // Check if we have a special mapping
  if (specialMappings[key]) {
    return specialMappings[key]
  }
  
  // Handle user.* prefixed keys
  if (key.startsWith('user.')) {
    const userKey = key.substring(5) // Remove 'user.' prefix
    if (specialMappings[`user.${userKey}`]) {
      return specialMappings[`user.${userKey}`]
    }
    // Format the user key part recursively
    const formattedUserKey = formatAttributeName(userKey)
    return `User ${formattedUserKey}`
  }
  
  // Convert camelCase to "Title Case"
  // Split on capital letters or underscores
  let formatted = key
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\s+/g, ' ') // Normalize multiple spaces
    .trim()
  
  // Handle common abbreviations
  formatted = formatted
    .replace(/\bId\b/gi, 'ID')
    .replace(/\bNo\b/gi, 'Number')
    .replace(/\bNum\b/gi, 'Number')
    .replace(/\bKg\b/gi, 'Kilograms')
    .replace(/\bM\b(?!\w)/gi, 'Meters') // M at end of word
    .replace(/\bM3\b/gi, 'Cubic Meters')
    .replace(/\bEmail\b/gi, 'Email')
    .replace(/\bPhone\b/gi, 'Phone')
    .replace(/\bUrl\b/gi, 'URL')
    .replace(/\bApi\b/gi, 'API')
    .replace(/\bKyc\b/gi, 'KYC')
    .replace(/\bVat\b/gi, 'VAT')
  
  // Capitalize first letter of each word
  formatted = formatted
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
  
  return formatted
}

